import React, { useMemo, useState } from "react";
import { X, Plus, Trash, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const WEEKDAYS = [
  { i: 0, short: "Mon", full: "Monday" },
  { i: 1, short: "Tue", full: "Tuesday" },
  { i: 2, short: "Wed", full: "Wednesday" },
  { i: 3, short: "Thu", full: "Thursday" },
  { i: 4, short: "Fri", full: "Friday" },
  { i: 5, short: "Sat", full: "Saturday" },
  { i: 6, short: "Sun", full: "Sunday" },
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * TemplatesModal — lets the user describe their posting cadence and generates
 * placeholder posts across the calendar. The user brings their own post types
 * (no defaults) and a repeating sequence with counts, e.g. Attract × 3 →
 * Nurture × 1 → Position × 2 → Convert × 1 loops across their posting weekdays.
 */
export default function TemplatesModal({ open, onClose, onDone }) {
  const [weekdays, setWeekdays] = useState([1, 3]); // Tue, Thu — user's example
  const [sequence, setSequence] = useState([{ type: "", count: 1 }]);
  const [startDate, setStartDate] = useState(todayISO());
  // 3-month default is a safer starting point than 12 — the user can bump it
  // up before generating if they want a longer runway. Prevents accidental
  // bulk creation of ~100+ placeholders on their very first click.
  const [months, setMonths] = useState(3);
  const [saving, setSaving] = useState(false);

  const totalCycle = useMemo(() => sequence.reduce((n, s) => n + Math.max(1, +s.count || 1), 0), [sequence]);
  const preview = useMemo(() => {
    // Expand rotation and label first N posting days as a preview.
    const rot = [];
    sequence.forEach(s => {
      const t = (s.type || "").trim();
      if (!t) return;
      for (let i = 0; i < Math.max(1, +s.count || 1); i++) rot.push(t);
    });
    if (!rot.length) return [];
    const wd = [...weekdays].sort();
    const out = [];
    const start = startDate ? new Date(startDate + "T00:00:00") : new Date();
    const cur = new Date(start);
    let rotI = 0;
    let scanned = 0;
    while (out.length < 6 && scanned < 60) {
      const w = (cur.getDay() + 6) % 7; // Mon=0…Sun=6
      if (wd.includes(w)) {
        out.push({
          date: `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`,
          type: rot[rotI % rot.length],
          weekday: WEEKDAYS[w].short,
        });
        rotI += 1;
      }
      cur.setDate(cur.getDate() + 1);
      scanned += 1;
    }
    return out;
  }, [sequence, weekdays, startDate]);

  const toggleWeekday = (i) => {
    setWeekdays(prev => (prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]));
  };

  const updateStep = (idx, patch) => {
    setSequence(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addStep = () => setSequence(prev => [...prev, { type: "", count: 1 }]);
  const removeStep = (idx) => setSequence(prev => prev.filter((_, i) => i !== idx));
  const moveStep = (idx, dir) => {
    setSequence(prev => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };

  const generate = async () => {
    const cleaned = sequence
      .map(s => ({ type: (s.type || "").trim(), count: Math.max(1, +s.count || 1) }))
      .filter(s => s.type);
    if (!weekdays.length) return toast.error("Pick at least one posting day.");
    if (!cleaned.length) return toast.error("Add at least one post type to your sequence.");
    setSaving(true);
    try {
      const r = await api.post("/planner/templates/generate", {
        start_date: startDate || null,
        weekdays,
        sequence: cleaned,
        months: Math.max(1, Math.min(24, +months || 12)),
      });
      toast.success(`Generated ${r.data.created} placeholders through ${r.data.range.end}.`);
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate placeholders.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="templates-modal"
        className="w-full max-w-3xl rounded-3xl p-6"
        style={{ background: "var(--bg-elev)", border: "1px solid var(--line)" }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar size={16} style={{ color: "var(--accent)" }} />
            <div>
              <div className="eyebrow" style={{ color: "var(--ink-3)" }}>Calendar Templates</div>
              <div className="font-serif text-2xl font-semibold" style={{ lineHeight: 1.1 }}>Plan your posting cadence</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[color:var(--paper)]" data-testid="templates-close"><X size={16} /></button>
        </div>

        <p className="text-sm mb-5" style={{ color: "var(--ink-3)" }}>
          Tell us which days you post and the sequence of post types you follow. We'll drop placeholder posts on
          every posting day so you always know what should go where. Placeholders disappear the moment you save a
          real edit; unedited placeholders are replaced when you regenerate.
        </p>

        <div className="mb-5">
          <div className="eyebrow mb-2">Which days do you post?</div>
          <div className="flex flex-wrap gap-1.5" data-testid="templates-weekdays">
            {WEEKDAYS.map(d => {
              const active = weekdays.includes(d.i);
              return (
                <button
                  key={d.i}
                  type="button"
                  data-testid={`templates-weekday-${d.short.toLowerCase()}`}
                  onClick={() => toggleWeekday(d.i)}
                  className="chip cursor-pointer text-xs"
                  style={active ? { background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" } : {}}
                >{d.short}</button>
              );
            })}
          </div>
        </div>

        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="eyebrow">Your posting sequence <span className="text-[10px] font-normal" style={{color:'var(--ink-3)'}}>· repeats every {totalCycle} post{totalCycle === 1 ? "" : "s"}</span></div>
            <button type="button" onClick={addStep} className="btn btn-ghost text-xs" data-testid="templates-add-step"><Plus size={12}/> Add type</button>
          </div>
          <div className="space-y-2" data-testid="templates-sequence">
            {sequence.map((s, i) => (
              <div
                key={i}
                className="flex items-center gap-2 p-2 rounded-2xl"
                style={{ background: "var(--paper)", border: "1px solid var(--line)" }}
                data-testid={`templates-step-${i}`}
              >
                <div className="w-6 text-xs font-mono" style={{ color: "var(--ink-3)" }}>#{i + 1}</div>
                <input
                  data-testid={`templates-step-type-${i}`}
                  className="input flex-1"
                  placeholder="Post type name (e.g. Attract, Nurture, Position, Convert, Case study…)"
                  value={s.type}
                  onChange={e => updateStep(i, { type: e.target.value })}
                />
                <span className="text-xs" style={{ color: "var(--ink-3)" }}>×</span>
                <input
                  data-testid={`templates-step-count-${i}`}
                  className="input"
                  type="number"
                  min={1}
                  max={50}
                  value={s.count}
                  onChange={e => updateStep(i, { count: e.target.value })}
                  style={{ width: 68 }}
                />
                <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0} className="p-1 text-xs" style={{ opacity: i === 0 ? 0.3 : 1 }} title="Move up">↑</button>
                <button type="button" onClick={() => moveStep(i, +1)} disabled={i === sequence.length - 1} className="p-1 text-xs" style={{ opacity: i === sequence.length - 1 ? 0.3 : 1 }} title="Move down">↓</button>
                <button type="button" onClick={() => removeStep(i)} className="p-1" data-testid={`templates-step-remove-${i}`} title="Remove"><Trash size={12} /></button>
              </div>
            ))}
          </div>
          <div className="text-xs mt-2" style={{ color: "var(--ink-3)" }}>
            Example — three Attract, one Nurture, two Position, one Convert = your cycle expands to 7 posts before it loops.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <label className="text-xs" style={{ color: "var(--ink-3)" }}>
            Start date
            <input
              type="date"
              data-testid="templates-start-date"
              className="input mt-1"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </label>
          <label className="text-xs" style={{ color: "var(--ink-3)" }}>
            Duration (months)
            <input
              type="number"
              min={1}
              max={24}
              data-testid="templates-months"
              className="input mt-1"
              value={months}
              onChange={e => setMonths(e.target.value)}
            />
          </label>
        </div>

        {preview.length > 0 && (
          <div className="mb-5 p-3 rounded-2xl" style={{ background: "var(--accent-tint)", border: "1px dashed var(--accent)" }} data-testid="templates-preview">
            <div className="eyebrow mb-2" style={{ color: "var(--accent)" }}>Preview — first 6 posting days</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              {preview.map((p, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-xl" style={{ background: "#fff", border: "1px solid var(--line)" }}>
                  <span className="font-mono" style={{ color: "var(--ink-3)" }}>{p.weekday} {p.date.slice(5)}</span>
                  <span className="chip text-[10px]" style={{ padding: "2px 8px" }}>{p.type}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4" style={{ borderTop: "1px solid var(--line)" }}>
          <div className="text-xs" style={{ color: "var(--ink-3)" }}>
            Regenerating replaces existing placeholders in this range. Real posts you've edited are never touched.
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button
              type="button"
              data-testid="templates-generate"
              onClick={generate}
              disabled={saving}
              className="btn btn-accent"
              style={saving ? { opacity: 0.6, cursor: "not-allowed" } : {}}
            >
              {saving ? "Generating…" : "Generate placeholders"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
